import CIQClient, { HTTPClientResponse } from "./ciq-client";

/** Design Scope feature type id for report-engine / update payloads. */
export const DESIGN_SCOPE_FEATURE_TYPE_ID = 10;

/** Shared body fields for create + update feature rows. */
export type DesignScopeInstanceFields = {
  title?: string | null;
  description?: string | null;
  classification_ids: Array<string | number>;
  classification: Array<{ title: string }>;
  /** Name. Omitted when `responsible_designer_id` is sent. */
  responsibility?: string;
  /** Catalog id of the Responsible Designer. Null clears it. */
  responsible_designer_id?: number | string | null;
  design_packages: Array<{
    design_package_id: string;
    design_package_milestone_id?: string | null;
    level_of_detail_value_id: string | number;
    notes?: string | null;
  }>;
  bid_packages: Array<{
    bid_package_id: string;
    is_primary?: boolean;
  }>;
};

/** One DESIGN_SCOPE row inside create `input.feature_instances`. */
export type DesignScopeCreateInstance = DesignScopeInstanceFields;

export type DesignScopeCreateInput = {
  feature_type: "DESIGN_SCOPE";
  feature_instances: DesignScopeCreateInstance[];
};

/** Success body from POST /features/createInstances for DESIGN_SCOPE. */
export type DesignScopeCreateSuccessData = {
  message?: string;
  ids?: string[];
};

/** One DESIGN_SCOPE row inside update `input.feature_instances`. */
export type DesignScopeUpdateInstance = {
  id: string;
  title?: string | null;
  description?: string | null;
  responsibility?: string;
  responsible_designer_id?: number | string | null;
  /** Omitted when linking an existing item to a package/milestone. */
  classification_ids?: DesignScopeInstanceFields["classification_ids"];
  classification?: DesignScopeInstanceFields["classification"];
  design_packages: DesignScopeInstanceFields["design_packages"];
  bid_packages: DesignScopeInstanceFields["bid_packages"];
};

export type DesignScopeUpdateInput = {
  feature_type: "DESIGN_SCOPE";
  feature_instance_type_id: number;
  feature_instances: DesignScopeUpdateInstance[];
};

/**
 * Create Design Scope feature instance(s).
 * Same `/features/...` + `{ input }` + project bearer pattern as
 * `linkBidPackages` (`/features/linkBidPackageV2`).
 *
 * Success is HTTP OK plus a non-empty `ids` array (error body TBD).
 */
export async function createDesignScopeInstances(
  input: DesignScopeCreateInput,
  projectToken: string
): Promise<HTTPClientResponse> {
  const response = await CIQClient.post({
    url: "/features/createInstances",
    data: { input },
    config: {
      headers: {
        Authorization: `Bearer ${projectToken}`
      }
    }
  });

  if (!response.success) return response;

  const data = response.data as DesignScopeCreateSuccessData | undefined;
  const ids = data?.ids;
  if (!Array.isArray(ids) || ids.length === 0) {
    return {
      ...response,
      success: false,
      errorMsg: data?.message || "Unable to create scope item."
    };
  }

  return response;
}

/** Success body from POST /features/update for DESIGN_SCOPE. */
export type DesignScopeUpdateSuccessData = {
  message?: string;
};

/**
 * Update Design Scope feature instance(s).
 * POST `/features/update` — success body: `{ message: string }`.
 */
export async function updateDesignScopeInstances(
  input: DesignScopeUpdateInput,
  projectToken: string
): Promise<HTTPClientResponse> {
  const response = await CIQClient.post({
    url: "/features/update",
    data: { input },
    config: {
      headers: {
        Authorization: `Bearer ${projectToken}`
      }
    }
  });

  if (!response.success) return response;

  const data = response.data as DesignScopeUpdateSuccessData | undefined;
  if (!data?.message) {
    return {
      ...response,
      success: false,
      errorMsg: "Unable to update scope item."
    };
  }

  return response;
}

export type DesignScopeDeleteInput = {
  feature_instance_ids: string[];
  feature_type_id: number;
};

/** Success body from POST /features/delete for DESIGN_SCOPE. */
export type DesignScopeDeleteSuccessData = {
  message?: string;
};

/**
 * Delete Design Scope feature instance(s).
 * POST `/features/delete` — body: `{ input: { feature_instance_ids, feature_type_id } }`.
 */
export async function deleteDesignScopeInstances(
  input: DesignScopeDeleteInput,
  projectToken: string
): Promise<HTTPClientResponse> {
  const response = await CIQClient.post({
    url: "/features/delete",
    data: { input },
    config: {
      headers: {
        Authorization: `Bearer ${projectToken}`
      }
    }
  });

  if (!response.success) return response;

  const data = response.data as DesignScopeDeleteSuccessData | undefined;
  if (!data?.message) {
    return {
      ...response,
      success: false,
      errorMsg: "Unable to delete scope item."
    };
  }

  return response;
}
